import React, { useState, useEffect, useMemo } from 'react';
import {
  IonPage,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonFooter,
} from '@ionic/react';
import {
  businessOutline,
  callOutline,
  chevronBackOutline,
  chevronForwardOutline,
  cardOutline,
  lockClosedOutline,
  logOut,
  locationOutline,
  mailOutline,
  personOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/authApi';
import { logoService } from '../services/logoService';
import api, { BACKEND_BASE_URL, PRIVACY_POLICY_URL } from '../services/api';
import { queryCache, queryKeys } from '../services/queryCache';
import HeaderLogo from '../components/common/HeaderLogo';
import AppHeader from '../components/common/AppHeader';
import { PageTitle } from '../components/common/PageTitle';
import './Profile.css';

function getInitials(name?: string | null, email?: string): string {
  const source = (name || email || '').trim();
  if (source.length >= 2) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return source.substring(0, 2).toUpperCase();
  }
  if (source.length === 1) return source.charAt(0).toUpperCase();
  return '?';
}

type ContractorProfileFields = {
  name: string;
  company: string;
  phone_number: string;
  email: string;
  business_address: string;
  license_number: string;
  preferred_branch: string;
  hourly_rate: string;
};

const emptyContractorProfile: ContractorProfileFields = {
  name: '',
  company: '',
  phone_number: '',
  email: '',
  business_address: '',
  license_number: '',
  preferred_branch: '',
  hourly_rate: '',
};

const normalizeContractorProfile = (profile: Partial<ContractorProfileFields> = {}): ContractorProfileFields => ({
  ...emptyContractorProfile,
  ...Object.fromEntries(
    Object.entries(profile).map(([key, value]) => [key, String(value ?? '')])
  ),
});

type SettingsView = 'home' | 'account' | 'workOrder' | 'security' | 'distributor' | 'privacy';
const CONTRACTOR_PROFILE_URL = `${BACKEND_BASE_URL.replace(/\/$/, '')}/api/contractor-profile`;

const Profile: React.FC = () => {
  const history = useHistory();
  const { user, logout, updateUser } = useAuth();
  const cachedContractorProfile = queryCache.getQueryData<any>(queryKeys.contractorProfile);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [distributorLogoUrl, setDistributorLogoUrl] = useState<string | null>(null);
  const [contractorProfile, setContractorProfile] = useState<ContractorProfileFields>(
    () => normalizeContractorProfile(cachedContractorProfile?.profile || {}),
  );
  const [contractorProfileLoading, setContractorProfileLoading] = useState(
    () => !cachedContractorProfile,
  );
  const [contractorProfileSaving, setContractorProfileSaving] = useState(false);
  const [contractorProfileMessage, setContractorProfileMessage] = useState<string | null>(null);
  const [contractorProfileError, setContractorProfileError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmationEmail, setDeleteConfirmationEmail] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [settingsView, setSettingsView] = useState<SettingsView>('home');

  const initials = useMemo(
    () => getInitials(user?.name, user?.email ?? ''),
    [user?.name, user?.email]
  );

  useEffect(() => {
    if (!user) {
      history.replace('/signin');
      return;
    }
    setName(user.name ?? '');
    setPhone(user.phone ?? '');
  }, [user, history]);

  useEffect(() => {
    if (user?.distributor?.logo_filename) {
      logoService.getLogoUrl(user.distributor.logo_filename).then(setDistributorLogoUrl);
    } else {
      setDistributorLogoUrl(null);
    }
  }, [user?.distributor?.logo_filename]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    if (!queryCache.getQueryData(queryKeys.contractorProfile)) {
      setContractorProfileLoading(true);
    }
    setContractorProfileError(null);
    queryCache.fetchQuery(
      queryKeys.contractorProfile,
      async () => (await api.get(CONTRACTOR_PROFILE_URL)).data,
      { staleTimeMs: 5 * 60 * 1000 },
    )
      .then((response) => {
        if (!active) return;
        setContractorProfile(normalizeContractorProfile(response?.profile || {}));
      })
      .catch((error) => {
        if (!active) return;
        setContractorProfileError(error?.response?.data?.message || error?.message || 'Failed to load work order details');
      })
      .finally(() => {
        if (active) setContractorProfileLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaveError(null);
    setSaving(true);
    try {
      const res = await authApi.updateProfile(user.id, { name: name || undefined, phone: phone || null });
      if (res.data) {
        updateUser({
          name: res.data.name,
          phone: res.data.phone,
        });
      }
    } catch (e: any) {
      setSaveError(e?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    setPasswordError(null);
    setChangingPassword(true);
    try {
      await authApi.updateProfile(user.id, {
        current_password: currentPassword,
        password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      setPasswordError(e?.message ?? 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const updateContractorProfileField = (field: keyof ContractorProfileFields, value: string) => {
    setContractorProfile((current) => ({ ...current, [field]: value }));
    setContractorProfileMessage(null);
    setContractorProfileError(null);
  };

  const handleSaveContractorProfile = async () => {
    setContractorProfileSaving(true);
    setContractorProfileError(null);
    setContractorProfileMessage(null);
    try {
      const response = await api.patch(CONTRACTOR_PROFILE_URL, {
        fields: contractorProfile,
        source: 'contractor settings',
      });
      const nextProfile = normalizeContractorProfile(response.data?.profile || contractorProfile);
      setContractorProfile(nextProfile);
      queryCache.setQueryData(queryKeys.contractorProfile, { profile: nextProfile });
      setContractorProfileMessage('Work order details saved');
    } catch (error: any) {
      setContractorProfileError(error?.response?.data?.message || error?.message || 'Failed to save work order details');
    } finally {
      setContractorProfileSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    history.replace('/signin');
  };

  const handleOpenPrivacyPolicy = async () => {
    try {
      await Browser.open({ url: PRIVACY_POLICY_URL });
    } catch (error) {
      window.open(PRIVACY_POLICY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setDeleteError(null);
    setDeletingAccount(true);

    try {
      await authApi.deleteAccount(deleteConfirmationEmail);
      logout();
      history.replace('/signin', {
        message: 'Your account has been deleted and you have been signed out.',
      });
    } catch (e: any) {
      setDeleteError(e?.message ?? 'Failed to delete your account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const closeDeleteModal = () => {
    if (deletingAccount) return;
    setShowDeleteModal(false);
    setDeleteConfirmationEmail('');
    setDeleteError(null);
  };

  const isSSO = !!user?.auth_provider;
  const deleteEmailMatches =
    deleteConfirmationEmail.trim().toLowerCase() === (user?.email ?? '').trim().toLowerCase();
  const workOrderProfileReady = Boolean(
    contractorProfile.company.trim()
    && contractorProfile.name.trim()
    && contractorProfile.phone_number.trim()
    && contractorProfile.email.trim()
    && contractorProfile.business_address.trim()
  );
  const settingsTitle = {
    home: 'Settings',
    account: 'Account',
    workOrder: 'Work order details',
    security: 'Security',
    distributor: 'Distributor',
    privacy: 'Privacy',
  }[settingsView];

  if (!user) return null;

  return (
    <IonPage>
      <AppHeader>
          <IonButtons slot="start">
            <IonButton onClick={() => settingsView === 'home' ? history.goBack() : setSettingsView('home')}>
              <IonIcon icon={chevronBackOutline} />
            </IonButton>
          </IonButtons>
          <HeaderLogo />
      </AppHeader>
      <IonContent>
        <div className="profile-content">
          <PageTitle>{settingsTitle}</PageTitle>

          {settingsView === 'home' && (
          <section className="profile-section profile-initials-section">
            <div className="profile-initials-circle profile-initials-circle-large">
              {initials}
            </div>
          </section>
          )}

          {settingsView === 'home' && (
            <section className="profile-section">
              <div className="settings-menu">
                <IonItem button className="settings-menu-row" lines="none" detail={false} onClick={() => setSettingsView('account')}>
                  <IonIcon icon={personOutline} slot="start" className="settings-menu-icon" aria-hidden="true" />
                  <IonLabel>
                    <span className="settings-menu-title">Account</span>
                    <span className="settings-menu-subtitle">{user.email}</span>
                  </IonLabel>
                  <IonIcon icon={chevronForwardOutline} slot="end" className="settings-menu-chevron" aria-hidden="true" />
                </IonItem>
                <IonItem button className="settings-menu-row" lines="none" detail={false} onClick={() => setSettingsView('workOrder')}>
                  <IonIcon icon={cardOutline} slot="start" className="settings-menu-icon" aria-hidden="true" />
                  <IonLabel>
                    <span className="settings-menu-title">Work order details</span>
                    <span className="settings-menu-subtitle">
                      {contractorProfileLoading ? 'Loading...' : workOrderProfileReady ? 'Ready for professional PDFs' : 'Missing letterhead details'}
                    </span>
                  </IonLabel>
                  <IonIcon icon={chevronForwardOutline} slot="end" className="settings-menu-chevron" aria-hidden="true" />
                </IonItem>
                <IonItem button className="settings-menu-row" lines="none" detail={false} onClick={() => setSettingsView('security')}>
                  <IonIcon icon={lockClosedOutline} slot="start" className="settings-menu-icon" aria-hidden="true" />
                  <IonLabel>
                    <span className="settings-menu-title">Security</span>
                    <span className="settings-menu-subtitle">{isSSO ? 'Managed by sign-in provider' : 'Password settings'}</span>
                  </IonLabel>
                  <IonIcon icon={chevronForwardOutline} slot="end" className="settings-menu-chevron" aria-hidden="true" />
                </IonItem>
                {user.distributor && (
                  <IonItem button className="settings-menu-row" lines="none" detail={false} onClick={() => setSettingsView('distributor')}>
                    <IonIcon icon={businessOutline} slot="start" className="settings-menu-icon" aria-hidden="true" />
                    <IonLabel>
                      <span className="settings-menu-title">Distributor</span>
                      <span className="settings-menu-subtitle">{user.distributor.name}</span>
                    </IonLabel>
                    <IonIcon icon={chevronForwardOutline} slot="end" className="settings-menu-chevron" aria-hidden="true" />
                  </IonItem>
                )}
                <IonItem button className="settings-menu-row" lines="none" detail={false} onClick={() => setSettingsView('privacy')}>
                  <IonIcon icon={lockClosedOutline} slot="start" className="settings-menu-icon" aria-hidden="true" />
                  <IonLabel>
                    <span className="settings-menu-title">Privacy</span>
                    <span className="settings-menu-subtitle">Policy and account deletion</span>
                  </IonLabel>
                  <IonIcon icon={chevronForwardOutline} slot="end" className="settings-menu-chevron" aria-hidden="true" />
                </IonItem>
              </div>
            </section>
          )}

          {settingsView === 'account' && (
          <section className="profile-section">
            <div className="profile-section-title">Account</div>
            <IonItem className="profile-email-card" lines="none" detail={false}>
              <IonIcon icon={mailOutline} slot="start" className="profile-email-icon" aria-hidden="true" />
              <IonLabel className="profile-email-label">
                <span className="profile-email-caption">Signed in as</span>
                <span className="profile-email-value">{user.email}</span>
              </IonLabel>
            </IonItem>
            <IonItem className="profile-input no-border" lines="none">
              <IonIcon icon={personOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
              <IonInput
                value={name}
                onIonInput={(e) => setName(e.detail.value ?? '')}
                label="Name"
                labelPlacement="floating"
                placeholder="Your name"
                clearOnEdit={false}
              />
            </IonItem>
            <IonItem className="profile-input no-border" lines="none">
              <IonIcon icon={callOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
              <IonInput
                type="tel"
                value={phone}
                onIonInput={(e) => setPhone(e.detail.value ?? '')}
                label="Phone"
                labelPlacement="floating"
                placeholder="Phone number"
                clearOnEdit={false}
              />
            </IonItem>
          {saveError && (
            <IonText color="danger">
              <p style={{ marginTop: 8 }}>{saveError}</p>
            </IonText>
          )}
          <IonButton expand="block" onClick={handleSaveProfile} disabled={saving} style={{ marginTop: 16 }}>
            {saving ? <IonSpinner name="crescent" /> : 'Save changes'}
          </IonButton>
        </section>
          )}

        {settingsView === 'workOrder' && (
        <section className="profile-section">
          <div className="profile-section-title">Work order details</div>
          <p className="profile-section-helper">
            Used on generated work orders as your contractor letterhead and default technician details.
          </p>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={businessOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              value={contractorProfile.company}
              onIonInput={(e) => updateContractorProfileField('company', e.detail.value ?? '')}
              label="Company"
              labelPlacement="floating"
              placeholder="Company name"
              clearOnEdit={false}
            />
          </IonItem>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={personOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              value={contractorProfile.name}
              onIonInput={(e) => updateContractorProfileField('name', e.detail.value ?? '')}
              label="Technician"
              labelPlacement="floating"
              placeholder="Technician name"
              clearOnEdit={false}
            />
          </IonItem>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={callOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              type="tel"
              value={contractorProfile.phone_number}
              onIonInput={(e) => updateContractorProfileField('phone_number', e.detail.value ?? '')}
              label="Phone"
              labelPlacement="floating"
              placeholder="Company phone"
              clearOnEdit={false}
            />
          </IonItem>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={mailOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              type="email"
              value={contractorProfile.email}
              onIonInput={(e) => updateContractorProfileField('email', e.detail.value ?? '')}
              label="Email"
              labelPlacement="floating"
              placeholder="Company email"
              clearOnEdit={false}
            />
          </IonItem>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={locationOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              value={contractorProfile.business_address}
              onIonInput={(e) => updateContractorProfileField('business_address', e.detail.value ?? '')}
              label="Business address"
              labelPlacement="floating"
              placeholder="Business address"
              clearOnEdit={false}
            />
          </IonItem>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={cardOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              value={contractorProfile.license_number}
              onIonInput={(e) => updateContractorProfileField('license_number', e.detail.value ?? '')}
              label="License"
              labelPlacement="floating"
              placeholder="License number"
              clearOnEdit={false}
            />
          </IonItem>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={cardOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              type="text"
              inputmode="decimal"
              value={contractorProfile.hourly_rate}
              onIonInput={(e) => updateContractorProfileField('hourly_rate', e.detail.value ?? '')}
              label="Hourly rate"
              labelPlacement="floating"
              placeholder="Hourly rate"
              clearOnEdit={false}
            />
          </IonItem>
          <IonItem className="profile-input no-border" lines="none">
            <IonIcon icon={businessOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
            <IonInput
              value={contractorProfile.preferred_branch}
              onIonInput={(e) => updateContractorProfileField('preferred_branch', e.detail.value ?? '')}
              label="Preferred branch"
              labelPlacement="floating"
              placeholder="Preferred branch"
              clearOnEdit={false}
            />
          </IonItem>
          {contractorProfileLoading && (
            <IonText color="medium">
              <p className="profile-inline-message">Loading work order details...</p>
            </IonText>
          )}
          {contractorProfileMessage && (
            <IonText color="success">
              <p className="profile-inline-message">{contractorProfileMessage}</p>
            </IonText>
          )}
          {contractorProfileError && (
            <IonText color="danger">
              <p className="profile-inline-message">{contractorProfileError}</p>
            </IonText>
          )}
          <IonButton
            expand="block"
            onClick={handleSaveContractorProfile}
            disabled={contractorProfileSaving || contractorProfileLoading}
            style={{ marginTop: 16 }}
          >
            {contractorProfileSaving ? <IonSpinner name="crescent" /> : 'Save work order details'}
          </IonButton>
        </section>
        )}

        {settingsView === 'security' && (
        <>
        {isSSO ? (
          <section className="profile-section">
            <div className="profile-section-title">Password</div>
            <IonText color="medium">
              <p style={{ margin: 0 }}>
                {(() => {
                  const provider = user.auth_provider?.toLowerCase();
                  const name = provider === 'google' ? 'Google' : provider === 'apple' ? 'Apple' : 'your provider';
                  return `You signed in with ${name}. Manage your account and password in your ${name} account settings.`;
                })()}
              </p>
            </IonText>
          </section>
        ) : (
          <section className="profile-section">
            <div className="profile-section-title">Change password</div>
            <IonItem className="profile-input no-border" lines="none">
              <IonIcon icon={lockClosedOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
              <IonInput
                type="password"
                value={currentPassword}
                onIonInput={(e) => setCurrentPassword(e.detail.value ?? '')}
                label="Current password"
                labelPlacement="floating"
                placeholder="Current password"
                clearOnEdit={false}
              />
            </IonItem>
            <IonItem className="profile-input no-border" lines="none">
              <IonIcon icon={lockClosedOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
              <IonInput
                type="password"
                value={newPassword}
                onIonInput={(e) => setNewPassword(e.detail.value ?? '')}
                label="New password"
                labelPlacement="floating"
                placeholder="New password"
                clearOnEdit={false}
              />
            </IonItem>
            <IonItem className="profile-input no-border" lines="none">
              <IonIcon icon={lockClosedOutline} slot="start" className="profile-field-icon" aria-hidden="true" />
              <IonInput
                type="password"
                value={confirmPassword}
                onIonInput={(e) => setConfirmPassword(e.detail.value ?? '')}
                label="Confirm new password"
                labelPlacement="floating"
                placeholder="Confirm new password"
                clearOnEdit={false}
              />
            </IonItem>
            {passwordError && (
              <IonText color="danger">
                <p style={{ marginTop: 8 }}>{passwordError}</p>
              </IonText>
            )}
            <IonButton expand="block" onClick={handleChangePassword} disabled={changingPassword} fill="outline" style={{ marginTop: 16 }}>
              {changingPassword ? <IonSpinner name="crescent" /> : 'Change password'}
            </IonButton>
          </section>
        )}
        </>
        )}

        {settingsView === 'distributor' && user.distributor && (
          <section className="profile-section">
            <div className="profile-section-title">Distributor</div>
            <div className="profile-distributor-card">
              {distributorLogoUrl ? (
                <img className="profile-distributor-logo" src={distributorLogoUrl} alt="" />
              ) : (
                <div className="profile-initials-circle profile-initials-circle-small">
                  {user.distributor.name.substring(0, 2).toUpperCase()}
                </div>
              )}
              <IonText>
                <strong>{user.distributor.name}</strong>
              </IonText>
            </div>
          </section>
        )}

        {settingsView === 'privacy' && (
        <section className="profile-section">
          <div className="profile-section-title">Privacy</div>
          <div className="profile-danger-card">
            <IonText color="dark">
              <p style={{ marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
                Deleting your account is permanent. You will lose access immediately. We remove
                personal account details, but SmartDocs may retain anonymized business records for
                distributor analytics, operations, and legal compliance.
              </p>
            </IonText>
            <IonButton expand="block" fill="outline" color="medium" onClick={handleOpenPrivacyPolicy}>
              View Privacy Policy
            </IonButton>
            <IonButton
              expand="block"
              color="danger"
              fill="outline"
              onClick={() => setShowDeleteModal(true)}
              style={{ marginTop: 12 }}
            >
              Delete Account
            </IonButton>
          </div>
        </section>
        )}

        {settingsView === 'home' && (
        <div className="profile-button-group">
          {user.role === 'SubscriptionUser' && (
            <IonButton expand="block" fill="outline" onClick={() => history.push('/subscribe')}>
              Manage Subscriptions
            </IonButton>
          )}
          <IonButton expand="block" color="medium" onClick={handleLogout}>
            <IonIcon icon={logOut} slot="start" />
            Logout
          </IonButton>
        </div>
        )}
        </div>
      </IonContent>
      <IonModal isOpen={showDeleteModal} onDidDismiss={closeDeleteModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Delete Account</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText color="danger">
            <p style={{ marginTop: 0, fontWeight: 600 }}>
              This permanently deletes your SmartDocs account access.
            </p>
          </IonText>
          <IonText color="medium">
            <p style={{ lineHeight: 1.5 }}>
              To confirm, type <strong>{user.email}</strong>. Personal account details will be
              anonymized, and some anonymized business records may be retained for distributor
              analytics and compliance.
            </p>
          </IonText>
          <IonItem className="profile-input no-border" lines="none" style={{ marginTop: 16 }}>
            <IonInput
              value={deleteConfirmationEmail}
              onIonInput={(e) => setDeleteConfirmationEmail(e.detail.value ?? '')}
              label="Confirm email"
              labelPlacement="floating"
              placeholder={user.email}
              clearOnEdit={false}
              autocomplete="email"
            />
          </IonItem>
          {deleteError && (
            <IonText color="danger">
              <p style={{ marginTop: 12 }}>{deleteError}</p>
            </IonText>
          )}
        </IonContent>
        <IonFooter>
          <div style={{ display: 'flex', gap: 12, padding: 16 }}>
            <IonButton expand="block" fill="outline" color="medium" onClick={closeDeleteModal} disabled={deletingAccount}>
              Cancel
            </IonButton>
            <IonButton
              expand="block"
              color="danger"
              onClick={handleDeleteAccount}
              disabled={!deleteEmailMatches || deletingAccount}
            >
              {deletingAccount ? <IonSpinner name="crescent" /> : 'Delete Account'}
            </IonButton>
          </div>
        </IonFooter>
      </IonModal>
    </IonPage>
  );
};

export default Profile;
